import React, { useState, useRef, useEffect } from 'react';
import ProgressSidebar from './ProgressSidebar';
import BrandLogo from './BrandLogo';

/**
 * Shell layout for the Personal Data Sheet form wizard.
 *
 * Renders the sidebar on large screens and a mobile progress bar on small
 * screens, plus the main content area with Back / Next navigation buttons.
 *
 * `stepVisible` is a parallel boolean array over `steps` — the spouse steps
 * are hidden when the client has no spouse or partner. All step counting
 * (progress percentage, "Step N of M") is done over visible steps only, so
 * the numbers stay contiguous either way.
 */
export default function WizardLayout({
  currentStep,
  steps,
  onBack,
  onNext,
  isFirst,
  isLast,
  nextDisabled = false,
  submitLabel = 'Submit',
  saving,
  onStepClick,
  stepStatuses,
  stepVisible = [],
  clientName,
  onLogout,
  children,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const isVisible = (idx) => stepVisible[idx] !== false;
  const visibleCount = steps.filter((_s, idx) => isVisible(idx)).length;

  const welcomeIdx = steps.findIndex((s) => s.key === 'welcome');
  const isWelcome = currentStep === welcomeIdx;

  // Exclude the welcome step from the count so it reads naturally.
  const progressSteps = Math.max(visibleCount - 1, 1);
  const stepNumber = steps
    .slice(0, currentStep + 1)
    .filter((_s, idx) => isVisible(idx)).length - 1;
  const pct = isWelcome
    ? 0
    : Math.round((stepNumber / progressSteps) * 100);

  return (
    <div className="cashflow-wizard-shell d-flex vh-100">
      <ProgressSidebar
        currentStep={currentStep}
        steps={steps}
        onStepClick={onStepClick}
        stepStatuses={stepStatuses}
        stepVisible={stepVisible}
      />

      <div className="cashflow-main flex-fill d-flex flex-column overflow-hidden">
        <header className="cashflow-topbar d-flex align-items-center px-4 py-2 border-bottom">
          <span className="d-lg-none me-2"><BrandLogo size={28} /></span>
          <span className="fw-semibold cashflow-brand-text d-lg-none me-auto small">
            Data Sheet
          </span>
          <span className="text-muted small ms-auto d-none d-lg-block">
            Logged in as <strong>{clientName}</strong>
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm text-muted ms-3 p-0"
            onClick={onLogout}
            title="Sign out"
            aria-label="Sign out"
          >
            <i className="bi bi-box-arrow-right" />
          </button>
        </header>

        <div className="d-lg-none px-3 pt-2 position-relative" ref={menuRef}>
          <div className="d-flex justify-content-between align-items-center small text-muted mb-1">
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-outline-secondary p-0 px-1"
                style={{ lineHeight: 1.2, fontSize: '1rem' }}
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Open step menu"
                aria-expanded={menuOpen}
              >
                <i className={`bi ${menuOpen ? 'bi-x-lg' : 'bi-list'}`} />
              </button>
              <span>{isWelcome ? 'Welcome' : `Step ${stepNumber} of ${progressSteps}`}</span>
            </div>
            <span>{steps[currentStep]?.label}</span>
          </div>
          <div className="progress" style={{ height: 6 }}>
            <div
              className="progress-bar bg-danger"
              style={{ width: `${pct}%` }}
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>

          {menuOpen && (
            <div
              className="position-absolute start-0 end-0 bg-white border border-top-0 shadow-sm"
              style={{ top: '100%', zIndex: 1050, maxHeight: '60vh', overflowY: 'auto' }}
            >
              {(() => {
                let displayNo = 0;
                return steps.map((step, idx) => {
                  if (!isVisible(idx)) return null;
                  if (idx > 0) displayNo += 1;

                  const isActive = idx === currentStep;
                  const status = stepStatuses?.[idx] || 'empty';
                  return (
                    <button
                      key={step.key}
                      className={`d-flex align-items-center gap-2 w-100 text-start btn btn-sm px-3 py-2 border-0 rounded-0 ${isActive ? 'bg-danger text-white fw-semibold' : 'bg-white text-dark'}`}
                      style={{ borderBottom: '1px solid #eee' }}
                      onClick={() => {
                        setMenuOpen(false);
                        onStepClick && onStepClick(idx);
                      }}
                    >
                      <span style={{ width: 20, textAlign: 'center', fontSize: '0.75rem' }}>
                        {!isActive && status === 'complete' ? (
                          <i className="bi bi-check-lg text-success" />
                        ) : !isActive && status === 'partial' ? (
                          <i className="bi bi-pencil-fill text-warning" style={{ fontSize: '0.65rem' }} />
                        ) : idx === 0 ? (
                          <i className={`bi ${step.icon}`} style={{ fontSize: '0.7rem' }} />
                        ) : (
                          <span className="fw-semibold">{displayNo}</span>
                        )}
                      </span>
                      <span className="small">{step.label}</span>
                    </button>
                  );
                });
              })()}
            </div>
          )}
        </div>

        <main className="cashflow-step-content flex-fill overflow-auto px-4 py-4">
          {children}
        </main>

        <footer className="cashflow-footer border-top px-4 py-3 d-flex justify-content-between align-items-center">
          <button
            className="btn btn-outline-secondary px-4 cashflow-nav-btn"
            onClick={onBack}
            disabled={isFirst || saving}
          >
            Back
          </button>

          <div className="flex-fill mx-3" style={{ maxWidth: 400 }}>
            <div className="position-relative">
              <div className="progress" style={{ height: 22, borderRadius: 12 }}>
                <div
                  className="progress-bar bg-danger"
                  style={{ width: `${pct}%`, borderRadius: 12, transition: 'width 0.3s ease' }}
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <span
                className="position-absolute top-50 start-50 translate-middle fw-semibold"
                style={{ fontSize: '0.75rem', color: pct > 45 ? '#fff' : '#666', zIndex: 1 }}
              >
                {isWelcome ? 'Welcome' : `${stepNumber} / ${progressSteps}`}
              </span>
            </div>
          </div>

          <button
            className={`btn ${isLast ? 'btn-success' : 'btn-danger'} px-4 cashflow-nav-btn`}
            onClick={onNext}
            disabled={saving || nextDisabled}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : isLast ? (
              <>
                {submitLabel} <i className="bi bi-check-lg ms-1" />
              </>
            ) : (
              'Next'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
